import {
  Card,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import useApiEp from "../hooks/useApiEp";

const WeeklyCreditsCard = ({
  title = "Weekly Credits Consumption",
  chipLabel = "Last 7 days",
  height = 300,
  colors,
}) => {
  const { data, isLoading, isError, error } = useApiEp(
    "/user/weekly-credits-consumption"
  );

  // Day abbreviations
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Normalize API data for chart
  const chartData =
    data?.data?.map((item) => ({
      day: item.dayLabel, // Use the label from API (e.g., "Mon 10" or just "Mon")
      credits: item.credits,
      fullDate: item.date, // For tooltip
    })) || [];

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        background: `
              linear-gradient(135deg,
                rgba(255, 255, 255, 0.95) 0%,
                rgba(248, 250, 252, 0.9) 100%
              )
            `,
        backdropFilter: "blur(20px)",
        boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 12px 28px rgba(16, 185, 129, 0.15)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Corner accent */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 120,
          height: 120,
          background:
            "radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          position: "relative",
          zIndex: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.textPrimary,
            letterSpacing: "0.5px",
            "&::after": {
              content: '""',
              display: "block",
              width: 48,
              height: 3,
              background: `linear-gradient(90deg, ${colors.success}, transparent)`,
              borderRadius: 3,
              mt: 1,
            },
          }}
        >
          {title}
        </Typography>

        <Chip
          label={chipLabel}
          size="small"
          sx={{
            background: "rgba(16, 185, 129, 0.08)",
            color: colors.textPrimary,
            border: "1px solid rgba(16, 185, 129, 0.2)",
            fontWeight: 500,
            backdropFilter: "blur(4px)",
          }}
        />
      </Box>

      {/* Content */}
      {isLoading && (
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={28} sx={{ color: colors.success }} />
        </Box>
      )}

      {isError && (
        <Alert severity="error">
          {error?.message || "Failed to load credits consumption"}
        </Alert>
      )}

      {!isLoading && !isError && (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="creditGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={colors.success}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={colors.success}
                  stopOpacity={0.2}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(99,102,241,0.08)"
              vertical={false}
            />

            <XAxis
              dataKey="day"
              tick={{ fill: colors.textSecondary, fontSize: "0.75rem" }}
              axisLine={{ stroke: "rgba(99,102,241,0.15)" }}
              tickLine={false}
            />

            <YAxis
              tick={{ fill: colors.textSecondary, fontSize: "0.75rem" }}
              axisLine={{ stroke: "rgba(99,102,241,0.15)" }}
              tickLine={false}
            />

            <Tooltip
              contentStyle={{
                background: "rgba(255,255,255,0.95)",
                border: `1px solid ${colors.success}30`,
                borderRadius: 12,
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                color: colors.textPrimary,
                backdropFilter: "blur(20px)",
              }}
              itemStyle={{
                color: colors.textPrimary,
                fontSize: "14px",
                fontWeight: "500",
              }}
              labelStyle={{
                color: colors.success,
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
              cursor={{ fill: "rgba(16, 185, 129, 0.1)" }}
              formatter={(value) => [`${value} credits`, ""]}
              labelFormatter={(label) => label}
            />

            <Bar dataKey="credits" barSize={32} radius={[6, 6, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill="url(#creditGradient)" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}

      {/* Bottom glow */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 60,
          background:
            "linear-gradient(to top, rgba(16,185,129,0.03), transparent)",
          pointerEvents: "none",
        }}
      />
    </Card>
  );
};

export default WeeklyCreditsCard;
